import {useTranslation} from '@aboutyou/i18n';
import {pickKeysLike} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC, useEffect, useRef} from 'react';
import {COLORS} from '../../../constants/colors';
import {FadePanel} from '../../atoms/FadePanel/FadePanel';
import {useSlider} from '../HorizontalSlider/hooks/useSlider';
import {Scrollbar} from '../Scrollbar/Scrollbar';
import {SliderBullets} from '../SliderBullets/SliderBullets';
import {NavButton} from './HorizontalNavigationControls';

interface HorizontalSimpleSliderProps {
  className?: string;
  dataTestId?: string;
  showNavButtons?: boolean;
  onClick?: () => void;
  navOverSlider?: boolean;
  onIndexChange?: (currentSlide: number) => void;
  initialSlide?: number;
  shouldReduceSliderHeight?: boolean;
  uniqueKey?: string;
  currentSlideIndicator?: 'bullets' | 'scrollbar' | 'none';
  scrollSnap?: 'center' | 'start';
  defaultSlidesNumber?: number;
  slideIndex?: number;
  enableFadePanel?: boolean;
  enableKeyboardNavigation?: boolean;
  children:
    | React.ReactElement<HTMLLIElement>
    | React.ReactElement<HTMLLIElement>[];
  [dataAttr: `data-${string}`]: string;
  CustomSlideControls?: React.ComponentType<{
    scrollToSlide: (
      index: number,
      options?: {behavior?: 'auto' | 'smooth'},
    ) => void;
    onPrev: () => void;
    onNext: () => void;
    isPrevActive: boolean;
    isNextActive: boolean;
  }>;
}

const FADE_PANEL_DARK_GRADIENT = 'rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 70%';

export const HorizontalSimpleSlider: FC<HorizontalSimpleSliderProps> = ({
  className,
  dataTestId = 'horizontalSimpleSlider',
  showNavButtons = false,
  onClick,
  navOverSlider = true,
  children,
  onIndexChange,
  initialSlide,
  shouldReduceSliderHeight = false,
  uniqueKey,
  currentSlideIndicator = 'bullets',
  defaultSlidesNumber,
  slideIndex,
  scrollSnap = 'center',
  CustomSlideControls,
  enableFadePanel,
  ...props
}) => {
  const dataAttrProps = pickKeysLike(props, 'data-');

  const {t} = useTranslation();
  const sliderRef = useRef<HTMLUListElement | null>(null);
  // This ref is used to calculate the correct width of the slider
  // in order to make swiping and navigation-bullets work correctly
  // It will be only provided to useSlider if the gallery is cropped.
  const firstChildRef = React.useRef<HTMLElement | null>(null);
  const {
    prev,
    next,
    currentSlide,
    slidesNumber: slidesNumberDynamic,
    scrollToSlide,
    prevIsActive,
    nextIsActive,
    leftoverOfViewport,
  } = useSlider(
    sliderRef,
    {uniqueKey},
    shouldReduceSliderHeight ? firstChildRef : undefined,
  );
  const slidesNumber = isNaN(slidesNumberDynamic)
    ? defaultSlidesNumber ?? NaN
    : slidesNumberDynamic;

  useEffect(() => {
    onIndexChange?.(currentSlide);
  }, [currentSlide, onIndexChange]);

  useEffect(() => {
    if (sliderRef.current && initialSlide !== undefined) {
      scrollToSlide(initialSlide, {behavior: 'auto'});
    }
  }, [sliderRef.current]);

  useEffect(() => {
    if (slideIndex !== undefined && slideIndex !== currentSlide) {
      scrollToSlide(slideIndex - 1, {behavior: 'auto'});
    }
  }, [slideIndex]);

  return (
    <SliderContainer
      className={className}
      data-testid={dataTestId}
      onClick={onClick}
      {...dataAttrProps}
      data-theme="dark"
    >
      <StyledFadePanel
        position={nextIsActive ? 'right' : 'left'}
        size={`${leftoverOfViewport}px`}
        disabled={!enableFadePanel}
        darkGradient={FADE_PANEL_DARK_GRADIENT}
      >
        <StyledSlider ref={sliderRef} tabIndex={0} $scrollSnap={scrollSnap}>
          {shouldReduceSliderHeight
            ? React.Children.map(children, (child, index) => {
                if (React.isValidElement(child)) {
                  if (index === 0) {
                    return React.cloneElement(
                      child as React.ReactElement<any>,
                      {
                        ref: (node: HTMLElement) =>
                          (firstChildRef.current = node),
                        key: index,
                      },
                    );
                  }
                  return child;
                }
                return null;
              })
            : children}
        </StyledSlider>
        {slidesNumber > 1 && (
          <>
            {CustomSlideControls && (
              <CustomSlideControls
                isPrevActive={prevIsActive}
                isNextActive={nextIsActive}
                onPrev={prev}
                onNext={next}
                scrollToSlide={scrollToSlide}
              />
            )}
            {!CustomSlideControls && (
              <NavContainer
                data-testid={`${dataTestId}SliderNav`}
                navOverSlider={navOverSlider}
                $showNavButtons={showNavButtons}
              >
                {showNavButtons && (
                  <NavButton
                    onClick={prev}
                    direction="prev"
                    aria-label={t('Go to the previous slide')}
                  />
                )}
                {currentSlideIndicator === 'bullets' && (
                  <StyledSliderBullets
                    data-testid={`${dataTestId}Bullets`}
                    navOverSlider={navOverSlider}
                    navBtnsShown={showNavButtons}
                    slidesNumber={slidesNumber}
                    currentSlide={currentSlide}
                  />
                )}
                {showNavButtons && (
                  <NavButton
                    onClick={next}
                    direction="next"
                    aria-label={t('Go to the next slide')}
                  />
                )}
              </NavContainer>
            )}
            {currentSlideIndicator === 'scrollbar' && (
              <StyledScrollbar
                thumbScrollLeft={slidesNumber / currentSlide}
                // Division by two in order to display the thumb correctly when in last
                // position (it will be wider and fit to the end of the scrollbar)
                thumbWidthFactor={slidesNumber / 2}
              />
            )}
          </>
        )}
      </StyledFadePanel>
    </SliderContainer>
  );
};

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const StyledSlider = styled.ul<{$scrollSnap: 'center' | 'start'}>`
  height: calc(
    100% - 64px
  ); // 64px is the height of the navigation buttons in mweb fullscreen mode
  display: flex;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  padding: 0;
  margin: 0;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: none !important;
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &:focus-visible {
    border: 2px solid Highlight;
    border: 2px solid -webkit-focus-ring-color;
  }

  // Wildcard helps add snap align for the css slider scroll, to jump always to the center
  // min width helps to force each slide to fill 100% of the slider.
  > * {
    min-width: 100%;
    position: relative;
    scroll-snap-align: ${props => props.$scrollSnap};
    scroll-snap-stop: always;

    list-style: none;
  }
`;

const NavContainer = styled.div<{
  navOverSlider: boolean;
  $showNavButtons: boolean;
}>`
  bottom: ${({navOverSlider}) => (!navOverSlider ? '18px' : 0)};
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  z-index: 1;

  background-color: ${({$showNavButtons}) =>
    $showNavButtons ? COLORS.White : ''};
`;

export const StyledSliderBullets = styled(SliderBullets)``;

export const StyledScrollbar = styled(Scrollbar)``;

const StyledFadePanel = styled(FadePanel)`
  display: block;
  height: 100%;
`;
